import * as THREE from 'three/webgpu';

/**
 * @file UV surgery on non-indexed geometry.
 *
 * HOUSE RULE: every factory in `prims.js` emits UVs **in metres** — one UV unit
 * per world unit. That makes tiling trivially predictable (`scaleUV(g, 1/2.5,
 * 1/2.5)` = one texture repeat every 2.5m) and it means UVs survive uniform
 * scaling in a sane way. Nothing here assumes 0..1 UVs EXCEPT
 * {@link setUVRect}, which is the atlas packer and says so.
 *
 * All functions mutate in place and return the geometry so they chain.
 *
 * @module engine/build/uvtools
 */

/**
 * @param {THREE.BufferGeometry} geo
 * @returns {THREE.BufferAttribute}
 */
function requireUV(geo) {
  let uv = geo.getAttribute('uv');
  if (!uv) {
    const count = geo.getAttribute('position')?.count ?? 0;
    uv = new THREE.BufferAttribute(new Float32Array(count * 2), 2);
    geo.setAttribute('uv', uv);
  }
  return /** @type {THREE.BufferAttribute} */ (uv);
}

/**
 * Tri-planar-ish box projection: each TRIANGLE is projected along its dominant
 * normal axis. The only sane default for merged architectural geometry — walls,
 * roofs and floors all get the same texel density with no authoring.
 *
 * Signs are chosen so opposite faces are not mirrored (text on a wall reads the
 * same way round a box).
 *
 * @param {THREE.BufferGeometry} geo Non-indexed. Indexed input throws — box UVs
 *   are inherently per-face and cannot be shared across an index buffer.
 * @param {number} [unitsPerTile=1] Metres per texture repeat.
 * @returns {THREE.BufferGeometry} `geo`
 */
export function boxUV(geo, unitsPerTile = 1) {
  if (geo.index !== null) throw new Error('boxUV: geometry must be non-indexed (call toFlat first)');
  const pos = /** @type {THREE.BufferAttribute} */ (geo.getAttribute('position'));
  if (!pos) return geo;
  const nrm = /** @type {THREE.BufferAttribute|undefined} */ (geo.getAttribute('normal'));
  const uv = requireUV(geo);
  const inv = 1 / (unitsPerTile || 1);
  const P = pos.array;
  const N = nrm ? nrm.array : null;
  const U = uv.array;

  for (let t = 0; t < pos.count; t += 3) {
    const i0 = t * 3;
    const i1 = (t + 1) * 3;
    const i2 = (t + 2) * 3;

    let nx;
    let ny;
    let nz;
    if (N) {
      nx = N[i0];
      ny = N[i0 + 1];
      nz = N[i0 + 2];
    } else {
      const ux = P[i1] - P[i0];
      const uy = P[i1 + 1] - P[i0 + 1];
      const uz = P[i1 + 2] - P[i0 + 2];
      const vx = P[i2] - P[i0];
      const vy = P[i2 + 1] - P[i0 + 1];
      const vz = P[i2 + 2] - P[i0 + 2];
      nx = uy * vz - uz * vy;
      ny = uz * vx - ux * vz;
      nz = ux * vy - uy * vx;
    }

    const ax = Math.abs(nx);
    const ay = Math.abs(ny);
    const az = Math.abs(nz);

    for (let v = 0; v < 3; v++) {
      const p = (t + v) * 3;
      const o = (t + v) * 2;
      const x = P[p];
      const y = P[p + 1];
      const z = P[p + 2];
      if (ax >= ay && ax >= az) {
        // Facing +/-X: (z, y), z flipped on the +X side so the pair mirrors.
        U[o] = (nx >= 0 ? -z : z) * inv;
        U[o + 1] = y * inv;
      } else if (ay >= az) {
        // Facing +/-Y: (x, z).
        U[o] = x * inv;
        U[o + 1] = (ny >= 0 ? -z : z) * inv;
      } else {
        // Facing +/-Z: (x, y).
        U[o] = (nz >= 0 ? x : -x) * inv;
        U[o + 1] = y * inv;
      }
    }
  }
  uv.needsUpdate = true;
  return geo;
}

/**
 * Single-axis planar projection — the right call for ground planes, decal
 * quads and heightfields where box projection would seam on steep slopes.
 * @param {THREE.BufferGeometry} geo
 * @param {'x'|'y'|'z'} [axis='y'] Axis to project ALONG (`'y'` = top-down).
 * @param {number} [scale=1] UV units per metre (so `1/tileMetres`).
 * @returns {THREE.BufferGeometry} `geo`
 */
export function planarUV(geo, axis = 'y', scale = 1) {
  const pos = /** @type {THREE.BufferAttribute} */ (geo.getAttribute('position'));
  if (!pos) return geo;
  const uv = requireUV(geo);
  const P = pos.array;
  const U = uv.array;
  for (let i = 0; i < pos.count; i++) {
    const p = i * 3;
    const o = i * 2;
    const x = P[p];
    const y = P[p + 1];
    const z = P[p + 2];
    if (axis === 'x') {
      U[o] = z * scale;
      U[o + 1] = y * scale;
    } else if (axis === 'z') {
      U[o] = x * scale;
      U[o + 1] = y * scale;
    } else {
      U[o] = x * scale;
      U[o + 1] = z * scale;
    }
  }
  uv.needsUpdate = true;
  return geo;
}

/**
 * Multiplies existing UVs. With the metres convention this is the retiler:
 * `scaleUV(geo, 1 / 2.5, 1 / 2.5)` = one repeat every 2.5 metres.
 * @param {THREE.BufferGeometry} geo
 * @param {number} su
 * @param {number} [sv=su]
 * @returns {THREE.BufferGeometry} `geo`
 */
export function scaleUV(geo, su, sv = su) {
  const uv = /** @type {THREE.BufferAttribute|undefined} */ (geo.getAttribute('uv'));
  if (!uv) return geo;
  const U = uv.array;
  for (let i = 0; i < U.length; i += 2) {
    U[i] *= su;
    U[i + 1] *= sv;
  }
  uv.needsUpdate = true;
  return geo;
}

/**
 * Remaps 0..1 UVs into an atlas sub-rect. **Assumes the geometry's UVs are
 * already normalised to 0..1** — geometry straight out of `prims.js` is in
 * metres, so normalise it first (e.g. `scaleUV(g, 1/w, 1/h)`) or this will
 * smear across the atlas.
 * @param {THREE.BufferGeometry} geo
 * @param {{ u0: number, v0: number, u1: number, v1: number }} rect
 * @returns {THREE.BufferGeometry} `geo`
 */
export function setUVRect(geo, { u0, v0, u1, v1 }) {
  const uv = /** @type {THREE.BufferAttribute|undefined} */ (geo.getAttribute('uv'));
  if (!uv) return geo;
  const du = u1 - u0;
  const dv = v1 - v0;
  const U = uv.array;
  for (let i = 0; i < U.length; i += 2) {
    U[i] = u0 + U[i] * du;
    U[i + 1] = v0 + U[i + 1] * dv;
  }
  uv.needsUpdate = true;
  return geo;
}
